import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2,
  ArrowLeft,
  Users,
  FileText,
  Gavel,
  RefreshCw,
  Eye,
  UserCog,
  UserCircle,
  Play,
  Settings,
  Clock,
  Crown,
  X,
  Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import AgencyMembershipTab from '../../components/platform/AgencyMembershipTab';
import AgencySettingsControlTab from '../../components/platform/AgencySettingsControlTab';
import AgencyUsersManagement from '../../components/platform/AgencyUsersManagement';
import {
  getEnterAgencyPath,
  getEnterAgencyPathFromSupportContext,
} from '../../utils/agencyType';
import AgencyClassificationBadge from '../../components/agency/AgencyClassificationBadge';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Agency Detail Page - Phase 8
 * 
 * Detailed view of a specific agency with:
 * - Overview stats
 * - Users list
 * - Cases list  
 * - Enter Agency / Support Client actions
 */
const AgencyDetailPage = () => {
  const { agencyId } = useParams();
  const { token, checkAuth } = useAuth();
  const { enterAgencyContext, isLoading: contextLoading } = useSupportContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [agency, setAgency] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [showDeleteStep1, setShowDeleteStep1] = useState(false);
  const [showDeleteStep2, setShowDeleteStep2] = useState(false);
  const [showDeleteStep3, setShowDeleteStep3] = useState(false);
  const [showDeleteNowModal, setShowDeleteNowModal] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [confirmChecks, setConfirmChecks] = useState({ authority: false, irreversible: false });
  const [deleteKeyword, setDeleteKeyword] = useState('');
  const [deleteNowName, setDeleteNowName] = useState('');
  const [deleteNowCode, setDeleteNowCode] = useState('');
  const [purgeName, setPurgeName] = useState('');
  const [purgeCode, setPurgeCode] = useState('');
  const [purgeError, setPurgeError] = useState('');
  const [deletionSubmitting, setDeletionSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [deleteNowError, setDeleteNowError] = useState('');
  const [scheduledDeleteAt, setScheduledDeleteAt] = useState(null);

  const handleUsersLoaded = useCallback(({ total, user_stats: loadedStats }) => {
    setAgency((prev) => {
      if (!prev) return prev;
      const nextStats = loadedStats
        ? {
            ...(prev.user_stats || {}),
            ...loadedStats,
            total:
              loadedStats.total_users
              ?? loadedStats.total
              ?? total
              ?? prev.user_stats?.total,
            total_users:
              loadedStats.total_users
              ?? loadedStats.total
              ?? total
              ?? prev.user_stats?.total_users,
          }
        : {
            ...(prev.user_stats || {}),
            total: typeof total === 'number' ? total : prev.user_stats?.total,
            total_users: typeof total === 'number' ? total : prev.user_stats?.total_users,
          };
      return { ...prev, user_stats: nextStats };
    });
  }, []);

  const extractErrorMessage = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object') return detail.message || detail.error || detail.code || fallback;
    return fallback;
  };

  const extractErrorCode = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === 'object') return detail.code || null;
    return null;
  };

  const fetchAgencyDetail = async () => {
    setLoading(true);
    try {
      const [agencyRes, casesRes] = await Promise.all([
        axios.get(`${API}/owner-console/agencies/${agencyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/owner-console/agencies/${agencyId}/cases?limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setAgency(agencyRes.data);
      setScheduledDeleteAt(agencyRes.data?.delete_after || null);
      setCases(casesRes.data.items || []);
    } catch (err) {
      console.error('Failed to fetch agency:', err);
      setError(err.response?.data?.detail || 'Failed to load agency');
    } finally {
      setLoading(false);
    }
  };

  const isPendingDeletion = agency?.deletion_status === 'pending_deletion' || agency?.status === 'pending_deletion';
  const isDeletedAgency = agency?.is_deleted === true || agency?.status === 'deleted' || agency?.deletion_status === 'deleted';

  const formatScheduledDate = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'long',
      });
    } catch {
      return String(value);
    }
  };

  useEffect(() => {
    if (token && agencyId) fetchAgencyDetail();
  }, [token, agencyId]);

  const handleEnterAgency = async () => {
    try {
      const result = await enterAgencyContext(agencyId, 'Platform owner support access');
      // Re-fetch /auth/me so Agency route guard sees canonical agency support scope.
      if (typeof checkAuth === 'function') {
        await checkAuth();
      }
      toast.success(`Entered agency context: ${agency?.name || agencyId}`);
      const agencyFallback = agency || { agency_id: agencyId };
      const path =
        (result?.redirect_path && String(result.redirect_path).startsWith('/')
          ? result.redirect_path
          : null)
        || (result?.redirect_to && String(result.redirect_to).startsWith('/')
          ? result.redirect_to
          : null)
        || getEnterAgencyPathFromSupportContext(result, agencyFallback)
        || getEnterAgencyPath(agencyFallback);
      navigate(path);
    } catch (err) {
      toast.error('Failed to enter agency context');
      console.error(err);
    }
  };

  const openDeleteStep1 = () => {
    setConfirmChecks({ authority: false, irreversible: false });
    setDeleteKeyword('');
    setShowDeleteStep2(false);
    setShowDeleteStep3(false);
    setShowDeleteStep1(true);
  };

  const scheduleDeletion = async () => {
    setDeletionSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/owner-console/agencies/${agencyId}/schedule-deletion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const deleteAfter = res.data?.delete_after;
      setAgency((prev) => ({
        ...(prev || {}),
        status: 'pending_deletion',
        deletion_status: 'pending_deletion',
        delete_after: deleteAfter,
      }));
      setScheduledDeleteAt(deleteAfter);
      setShowDeleteStep2(false);
      setShowDeleteStep3(true);
      toast.success('Sub-account scheduled for deletion');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule deletion');
    } finally {
      setDeletionSubmitting(false);
    }
  };

  const cancelDeletion = async () => {
    setDeletionSubmitting(true);
    setCancelError('');
    try {
      await axios.post(
        `${API}/owner-console/agencies/${agencyId}/cancel-deletion`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgency((prev) => ({
        ...(prev || {}),
        status: 'active',
        deletion_status: 'active',
        delete_after: null,
      }));
      setScheduledDeleteAt(null);
      setShowDeleteStep3(false);
      toast.success('Delete operation cancelled');
      await fetchAgencyDetail();
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to cancel delete operation');
      setCancelError(message);
      toast.error(message);
    } finally {
      setDeletionSubmitting(false);
    }
  };

  const openDeleteNowModal = () => {
    setDeleteNowName('');
    setDeleteNowCode('');
    setDeleteNowError('');
    setShowDeleteNowModal(true);
  };

  const deleteAgencyNow = async () => {
    setDeletionSubmitting(true);
    setDeleteNowError('');
    try {
      await axios.post(
        `${API}/owner-console/agencies/${agencyId}/delete-now`,
        {
          two_fa_code: deleteNowCode,
          confirmation_name: deleteNowName,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Agency deleted and removed from active agencies.');
      setShowDeleteNowModal(false);
      navigate('/platform/agencies');
    } catch (err) {
      const code = extractErrorCode(err);
      const message = extractErrorMessage(err, 'Failed to delete agency');
      setDeleteNowError(message);
      toast.error(message);
      if (code === 'TWO_FACTOR_NOT_CONFIGURED') {
        setDeleteNowError(
          `${message} Set up 2FA in Platform Settings > Security > Authentication.`
        );
      }
    } finally {
      setDeletionSubmitting(false);
    }
  };

  const openPurgeModal = () => {
    setPurgeName('');
    setPurgeCode('');
    setPurgeError('');
    setShowPurgeModal(true);
  };

  const purgeAgency = async () => {
    setDeletionSubmitting(true);
    setPurgeError('');
    try {
      await axios.post(
        `${API}/owner-console/agencies/${agencyId}/purge`,
        {
          two_fa_code: purgeCode,
          confirmation_name: purgeName,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Deleted agency permanently purged.');
      setShowPurgeModal(false);
      navigate('/platform/agencies');
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to purge agency');
      setPurgeError(message);
      toast.error(message);
    } finally {
      setDeletionSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/platform/agencies')}>
          <ArrowLeft size={18} className="mr-2" />
          Back to Agencies
        </Button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agency-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/platform/agencies')}>
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <Building2 className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">{agency?.name || 'Unnamed Agency'}</h1>
              <p className="text-sm text-slate-500 font-mono">{agencyId}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
            isDeletedAgency
              ? 'bg-slate-200 text-slate-700'
              : isPendingDeletion
                ? 'bg-rose-100 text-rose-700'
                : agency?.status === 'active'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-100 text-slate-600'
          }`}>
            {isDeletedAgency ? 'Deleted' : isPendingDeletion ? 'Pending Deletion' : (agency?.status || 'active')}
          </span>
          <AgencyClassificationBadge
            agency={agency}
            testId="agency-type-badge"
          />
          {!isDeletedAgency && !isPendingDeletion ? (
            <>
              <Button onClick={openDeleteStep1} variant="outline" size="sm" data-testid="agency-schedule-delete">
                <Trash2 size={15} className="mr-2" />
                Schedule Delete
              </Button>
              <Button onClick={openDeleteNowModal} variant="destructive" size="sm" data-testid="agency-delete-now">
                <Trash2 size={15} className="mr-2" />
                Delete Immediately
              </Button>
            </>
          ) : null}
          {!isDeletedAgency && isPendingDeletion ? (
            <Button onClick={() => { setCancelError(''); setShowDeleteStep3(true); }} variant="outline" size="sm" data-testid="agency-stop-delete">
              Stop Delete Operation
            </Button>
          ) : null}
          {isDeletedAgency ? (
            <>
              <span className="text-sm text-slate-500" data-testid="agency-deleted-readonly">
                Read-only deleted agency
              </span>
              <Button
                onClick={openPurgeModal}
                variant="destructive"
                size="sm"
                data-testid="agency-purge"
              >
                Permanently purge deleted agency
              </Button>
            </>
          ) : null}
          <Button 
            onClick={() => navigate(`/platform/agencies/${agencyId}/settings`)} 
            variant="outline" 
            size="sm"
            data-testid="agency-settings-btn"
          >
            <Settings size={16} className="mr-2" />
            Settings
          </Button>
          <Button onClick={fetchAgencyDetail} variant="outline" size="sm">
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Enter Agency Action */}
      <div className={`rounded-xl p-6 text-white ${isDeletedAgency ? 'bg-slate-500' : 'bg-gradient-to-r from-amber-500 to-amber-600'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Enter Agency Context</h2>
            <p className={`mt-1 ${isDeletedAgency ? 'text-slate-100' : 'text-amber-100'}`}>
              {isDeletedAgency
                ? 'This agency has been deleted. Enter Agency is disabled.'
                : 'Switch into this agency to provide support or manage configuration'}
            </p>
          </div>
          <Button
            onClick={handleEnterAgency}
            disabled={contextLoading || isDeletedAgency}
            className="bg-white text-amber-600 hover:bg-amber-50 disabled:opacity-60"
            data-testid="agency-enter-button"
          >
            {contextLoading ? (
              <div className="animate-spin w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full mr-2" />
            ) : (
              <Play size={18} className="mr-2" />
            )}
            Enter Agency
          </Button>
        </div>
      </div>

      {/* Stats Cards — Total Users = all active login accounts; Staff = admins + staff */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
          <Users className="mx-auto text-amber-500 mb-2" size={24} />
          <p className="text-2xl font-bold text-stone-900">
            {(agency?.user_stats?.total_users ?? agency?.user_stats?.total) || 0}
          </p>
          <p className="text-xs text-slate-500">Total Users</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
          <UserCog className="mx-auto text-emerald-500 mb-2" size={24} />
          <p className="text-2xl font-bold text-stone-900">{agency?.user_stats?.staff || 0}</p>
          <p className="text-xs text-slate-500">Staff</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
          <UserCircle className="mx-auto text-sky-500 mb-2" size={24} />
          <p className="text-2xl font-bold text-stone-900">{agency?.user_stats?.managed_clients ?? agency?.user_stats?.clients ?? 0}</p>
          <p className="text-xs text-slate-500">Managed Clients</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
          <FileText className="mx-auto text-emerald-500 mb-2" size={24} />
          <p className="text-2xl font-bold text-stone-900">{agency?.case_stats?.active || 0}</p>
          <p className="text-xs text-slate-500">Active Cases</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
          <Clock className="mx-auto text-amber-500 mb-2" size={24} />
          <p className="text-2xl font-bold text-stone-900">{agency?.recent_activity_count || 0}</p>
          <p className="text-xs text-slate-500">Today's Activity</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger value="membership" className="data-[state=active]:bg-white">
            <Crown size={14} className="mr-1" />
            Membership
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white">
            Users ({(agency?.user_stats?.total_users ?? agency?.user_stats?.total) || 0})
          </TabsTrigger>
          <TabsTrigger value="cases" className="data-[state=active]:bg-white">Cases ({agency?.case_stats?.total || 0})</TabsTrigger>
          <TabsTrigger value="settings-control" className="data-[state=active]:bg-white">
            <Settings size={14} className="mr-1" />
            Settings Control
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-stone-900 mb-4">Agency Information</h3>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Agency ID</dt>
                  <dd className="font-mono text-sm">{agencyId}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Status</dt>
                  <dd className="capitalize">{isPendingDeletion ? 'Pending Deletion' : (agency?.status || 'active')}</dd>
                </div>
                {isPendingDeletion && (
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Delete After</dt>
                    <dd>{formatScheduledDate(agency?.delete_after || scheduledDeleteAt)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-slate-500">Plan</dt>
                  <dd className="capitalize">{agency?.plan || 'Standard'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Created</dt>
                  <dd>{agency?.created_at ? new Date(agency.created_at).toLocaleDateString() : 'N/A'}</dd>
                </div>
              </dl>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-stone-900 mb-4">Owner Information</h3>
              {agency?.owner_info ? (
                <dl className="space-y-3">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Name</dt>
                    <dd>{agency.owner_info.name || 'N/A'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Email</dt>
                    <dd>{agency.owner_info.email || 'N/A'}</dd>
                  </div>
                </dl>
              ) : (
                <p className="text-slate-500">No owner information available</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="membership" className="mt-6">
          <AgencyMembershipTab agencyId={agencyId} />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <AgencyUsersManagement
            agencyId={agencyId}
            agencyName={agency?.name}
            listSource="owner-console"
            onUsersLoaded={handleUsersLoaded}
          />
        </TabsContent>

        <TabsContent value="cases" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {cases.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Case</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Client</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {cases.map((c) => (
                    <tr key={c.case_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-mono text-sm text-slate-700">{c.case_id}</p>
                      </td>
                      <td className="px-4 py-3">
                        {c.client_info ? (
                          <div>
                            <p className="font-medium text-stone-900">{c.client_info.name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{c.client_info.email}</p>
                          </div>
                        ) : (
                          <span className="text-slate-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600 capitalize">{c.case_type?.replace('_', ' ') || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          c.status === 'active' || c.status === 'in_progress'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {c.status || 'active'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-500">
                          {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-12 text-center">
                <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No cases in this agency</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings-control" className="mt-6">
          <AgencySettingsControlTab 
            agencyId={agencyId}
            agencyName={agency?.name}
          />
        </TabsContent>
      </Tabs>

      {showDeleteStep1 && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
            <div className="px-8 py-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-semibold text-slate-900">Delete Sub-Account {agency?.name || agencyId}</h3>
                <p className="text-slate-500 mt-3 text-lg">You need to complete a few easy steps to delete this location</p>
              </div>
              <button onClick={() => setShowDeleteStep1(false)} className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <X size={26} />
              </button>
            </div>
            <div className="px-8 py-8 border-b border-slate-200">
              <p className="text-2xl font-medium text-slate-800 mb-8">Are you sure you want to delete this sub-account?</p>
              <label className="flex items-center gap-4 text-lg text-slate-700 mb-5">
                <input
                  type="checkbox"
                  checked={confirmChecks.authority}
                  onChange={(e) => setConfirmChecks((p) => ({ ...p, authority: e.target.checked }))}
                />
                I confirm that I have discussed this with my client and have authority to take this action
              </label>
              <label className="flex items-center gap-4 text-lg text-slate-700">
                <input
                  type="checkbox"
                  checked={confirmChecks.irreversible}
                  onChange={(e) => setConfirmChecks((p) => ({ ...p, irreversible: e.target.checked }))}
                />
                I understand that this action is irreversible & all data inside this sub-account will be lost
              </label>
            </div>
            <div className="px-8 py-6 flex justify-end gap-4">
              <Button variant="outline" onClick={() => setShowDeleteStep1(false)}>Not Now</Button>
              <Button
                variant="destructive"
                disabled={!confirmChecks.authority || !confirmChecks.irreversible}
                onClick={() => {
                  setShowDeleteStep1(false);
                  setShowDeleteStep2(true);
                }}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDeleteStep2 && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
            <div className="px-8 py-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-3xl font-semibold text-slate-900">Confirm Account Deletion</h3>
              <button onClick={() => setShowDeleteStep2(false)} className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <X size={26} />
              </button>
            </div>
            <div className="px-8 py-8">
              <p className="text-lg text-slate-700 leading-relaxed">
                Deleting this sub-account will <span className="font-semibold">immediately stop all active operations, campaigns, automations, and services</span> associated with it.
              </p>
              <p className="text-lg text-slate-700 mt-2">This action is permanent and <span className="font-semibold">cannot be undone.</span></p>
              <p className="text-lg text-slate-800 mt-6">Type <span className="text-red-600 font-bold">DELETE</span> to confirm</p>
              <input
                value={deleteKeyword}
                onChange={(e) => setDeleteKeyword(e.target.value)}
                placeholder="Type here"
                className="mt-4 w-full border border-slate-300 rounded-xl px-4 py-4 text-lg"
              />
            </div>
            <div className="px-8 py-6 flex justify-end gap-4">
              <Button variant="outline" onClick={() => setShowDeleteStep2(false)}>Not Now</Button>
              <Button
                variant="destructive"
                disabled={deleteKeyword !== 'DELETE' || deletionSubmitting}
                onClick={scheduleDeletion}
                data-testid="agency-confirm-schedule-delete"
              >
                {deletionSubmitting ? 'Scheduling…' : 'Schedule Delete (24h)'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDeleteStep3 && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
            <div className="px-8 py-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-3xl font-semibold text-slate-900">This sub-account is scheduled for deletion</h3>
              <button onClick={() => setShowDeleteStep3(false)} className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <X size={26} />
              </button>
            </div>
            <div className="px-8 py-12 text-center border-b border-slate-200">
              <Trash2 size={44} className="mx-auto text-red-600 mb-4" />
              <h4 className="text-4xl font-semibold text-slate-900 mb-4">{agency?.name || agencyId}</h4>
              <p className="text-lg text-slate-700">This sub-account has been scheduled for deletion. It will be deleted by</p>
              <p className="text-2xl font-semibold text-slate-900 mt-3">{formatScheduledDate(scheduledDeleteAt || agency?.delete_after)}</p>
              <p className="text-lg text-slate-600 mt-3">You can change your mind before this time.</p>
              {cancelError ? (
                <p className="mt-4 text-red-600 text-sm" data-testid="agency-cancel-delete-error" role="alert">
                  {cancelError}
                </p>
              ) : null}
            </div>
            <div className="px-8 py-6 flex justify-end gap-4">
              <Button variant="outline" onClick={() => setShowDeleteStep3(false)}>Close</Button>
              <Button
                variant="destructive"
                onClick={cancelDeletion}
                disabled={deletionSubmitting}
                data-testid="agency-confirm-stop-delete"
              >
                {deletionSubmitting ? 'Stopping…' : 'Stop Delete Operation'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDeleteNowModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="agency-delete-now-modal">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-900">Delete Immediately</h3>
              <button onClick={() => setShowDeleteNowModal(false)} className="text-slate-500">
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-slate-700">
                This will delete this agency immediately. This action cannot be undone.
              </p>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Type agency name <span className="font-semibold">{agency?.name}</span> to confirm
                </label>
                <input
                  className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={deleteNowName}
                  onChange={(e) => setDeleteNowName(e.target.value)}
                  data-testid="agency-delete-now-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">2FA code</label>
                <input
                  className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 tracking-widest"
                  inputMode="numeric"
                  maxLength={6}
                  value={deleteNowCode}
                  onChange={(e) => setDeleteNowCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="agency-delete-now-2fa"
                />
              </div>
              {deleteNowError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="agency-delete-now-error" role="alert">
                  <p>{deleteNowError}</p>
                  {String(deleteNowError).includes('Two-factor') || String(deleteNowError).includes('2FA') ? (
                    <Button
                      type="button"
                      variant="link"
                      className="px-0 text-red-700"
                      onClick={() => navigate('/platform/settings/authentication')}
                      data-testid="agency-setup-2fa-link"
                    >
                      Set up 2FA
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowDeleteNowModal(false)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={
                  deletionSubmitting
                  || deleteNowName.trim().toLowerCase() !== String(agency?.name || '').trim().toLowerCase()
                  || deleteNowCode.length !== 6
                }
                onClick={deleteAgencyNow}
                data-testid="agency-confirm-delete-now"
              >
                {deletionSubmitting ? 'Deleting…' : 'Delete Immediately'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPurgeModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="agency-purge-modal">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-red-700">Permanently purge deleted agency</h3>
              <button onClick={() => setShowPurgeModal(false)} className="text-slate-500">
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-slate-700">
                This permanently removes the deleted agency record. Intended for test/demo cleanup only.
                Type the agency name and a valid 2FA code to continue.
              </p>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Type agency name <span className="font-semibold">{agency?.name}</span> to confirm
                </label>
                <input
                  className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2"
                  value={purgeName}
                  onChange={(e) => setPurgeName(e.target.value)}
                  data-testid="agency-purge-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">2FA code</label>
                <input
                  className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 tracking-widest"
                  inputMode="numeric"
                  maxLength={6}
                  value={purgeCode}
                  onChange={(e) => setPurgeCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="agency-purge-2fa"
                />
              </div>
              {purgeError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="agency-purge-error" role="alert">
                  {purgeError}
                </div>
              ) : null}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowPurgeModal(false)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={
                  deletionSubmitting
                  || purgeName.trim().toLowerCase() !== String(agency?.name || '').trim().toLowerCase()
                  || purgeCode.length !== 6
                }
                onClick={purgeAgency}
                data-testid="agency-confirm-purge"
              >
                {deletionSubmitting ? 'Purging…' : 'Permanently purge'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyDetailPage;
